import DeleteIcon from "@mui/icons-material/Delete";
import Button, { ButtonProps } from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import { SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";

interface AppButtonProps {
  children: string | JSX.Element;
  linkTo?: string;
  onClick?: () => void;
  disabled?: boolean;
  startIcon?: React.ReactNode;
}
interface BaseAppButtonProps {
  variant: BaseAppButtonVariant;
  color: BaseAppButtonColor;
  size: BaseAppButtonSize;
}

type BaseAppButtonVariant = "contained" | "outlined";
export type BaseAppButtonColor =
  | "inherit"
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "info"
  | "warning";
type BaseAppButtonSize = "large" | "regular" | "small";

const LargeButton = styled(Button)({
  height: "48px",
});

const SmallIconButton = styled(IconButton)({
  width: "24px",
  height: "24px",
});

export const ThickButton = styled(Button)<ButtonProps>(({ theme }) => ({
  border: `2px solid ${theme.palette.primary.light}`,
}));

const BaseAppButton = ({
  children,
  linkTo,
  onClick,
  variant,
  color,
  disabled,
  startIcon,
  size,
}: AppButtonProps & BaseAppButtonProps) => {
  if (linkTo && onClick) {
    throw new Error(
      `Button ${JSON.stringify(
        children
      )} cannot have linkTo and onClick properties`
    );
  }

  const navigate = useNavigate();

  const sizeComponentMap: { [key: string]: any } = {
    large: LargeButton,
    regular: Button,
    small: Button,
  };

  const ButtonComponent = sizeComponentMap[size];

  return (
    <ButtonComponent
      fullWidth
      size="large"
      variant={variant}
      color={color}
      onClick={onClick ? onClick : linkTo ? () => navigate(linkTo) : undefined}
      disabled={disabled}
      startIcon={startIcon}
    >
      {children}
    </ButtonComponent>
  );
};

export const SecondaryRaisedButton = (props: AppButtonProps) => {
  return (
    <BaseAppButton
      {...props}
      variant="contained"
      color="secondary"
      size="large"
    />
  );
};

export const SecondaryOutlinedButton = (props: AppButtonProps) => {
  return (
    <BaseAppButton
      {...props}
      variant="outlined"
      color="secondary"
      size="large"
    />
  );
};

export const PrimaryRaisedButton = (props: AppButtonProps) => {
  return (
    <BaseAppButton
      {...props}
      variant="contained"
      color="primary"
      size="large"
    />
  );
};

export const PrimaryOutlinedButton = (props: AppButtonProps) => {
  return (
    <BaseAppButton {...props} variant="outlined" color="primary" size="large" />
  );
};

export const WarningOutlinedButton = (props: AppButtonProps) => {
  return (
    <BaseAppButton
      {...props}
      variant="outlined"
      color="warning"
      size="regular"
    />
  );
};

export const DangerOutlinedButton = (props: AppButtonProps) => {
  return (
    <BaseAppButton {...props} variant="outlined" color="error" size="large" />
  );
};

export const DangerRaisedButton = (props: AppButtonProps) => {
  return (
    <BaseAppButton {...props} variant="contained" color="error" size="large" />
  );
};

export const PrimaryIconButton = ({
  children,
  linkTo,
  onClick,
}: AppButtonProps) => {
  if (linkTo && onClick) {
    throw new Error(
      `Button ${JSON.stringify(
        children
      )} cannot have linkTo and onClick properties`
    );
  }

  const navigate = useNavigate();

  return (
    <IconButton
      onClick={onClick ? onClick : linkTo ? () => navigate(linkTo) : undefined}
    >
      {children}
    </IconButton>
  );
};

export const PrimaryTextButton = ({ children, linkTo }: AppButtonProps) => {
  const navigate = useNavigate();

  return (
    <Button
      variant="text"
      onClick={linkTo ? () => navigate(linkTo) : undefined}
    >
      {children}
    </Button>
  );
};

export const DeleteIconButton = ({
  onClick,
}: {
  onClick?: (e: SyntheticEvent<Element, Event>) => void;
}) => {
  return (
    <SmallIconButton aria-label="delete" size="small" onClick={onClick}>
      <DeleteIcon />
    </SmallIconButton>
  );
};
